/**
 * The PGO headline of an attempt, one line per LAYER.
 *
 * "PGO: degraded" used to be one badge with one fixed sentence ("addon built
 * without a training profile, collection job …") whatever had actually
 * failed. Prod 2026-10-14: the type-profile collection SUCCEEDED and the real
 * failure was the LLVM layer ("llvm-pgo: instrumented compile failed (job
 * bb46255f): compile_failed"), yet the banner pointed at the collection job.
 * Now each layer says its own state and cause:
 *
 *   Type profile: ok (job b0e11027)
 *   LLVM PGO: failed — instrumented compile failed (job bb46255f): compile_failed
 *   LLVM PGO: disabled by operator (LLVM_PGO_ENABLED=false)
 *   Text order: package order applied (1234 entries)
 *
 * Source of truth is the run's step checklist (internal/publish
 * stepChecklist.ts: the rows type-profile, type-profile-training,
 * llvm-instrumented, llvm-training, text-order carry step 3's own reasons,
 * including "disabled by operator" and the instrumented-compile timeout/
 * failure text). An attempt without a checklist falls back to splitting the
 * stored pgo_degraded_reason by the prefixes combinePgoStatus writes
 * ("llvm-pgo:", "text-order:"; an unprefixed line is the type profile's).
 */
import { parseSteps, type StepRecord } from "./stepChecklist.ts";

export type PgoLayerName = "Type profile" | "LLVM PGO" | "Text order";
export type PgoLayerState = "ok" | "failed" | "disabled" | "off";

export interface PgoLayer {
  name: PgoLayerName;
  state: PgoLayerState;
  /** The cause (failed/disabled) or note (ok); first-class text, no prefix. */
  text: string;
  /** The compile job the layer is about (collection / instrumented). */
  jobId: string | null;
}

const CAUSE_PREFIXES = [
  /^PGO degraded:\s*/i,
  /^LLVM PGO degraded:\s*/i,
  /^llvm-pgo:\s*/i,
  /^text-order:\s*/i,
  /^type profile:\s*/i,
];

export function stripCausePrefix(text: string): string {
  let t = text.trim();
  for (let i = 0; i < 3; i++) {
    const before = t;
    for (const re of CAUSE_PREFIXES) t = t.replace(re, "");
    if (t === before) break;
  }
  return t;
}

const jobIn = (text: string): string | null =>
  /\bjob ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/i.exec(
    text,
  )?.[1] ?? null;

function lastJob(...rows: Array<StepRecord | undefined>): string | null {
  for (const r of rows)
    for (const b of [...(r?.builds ?? [])].reverse())
      if (b.jobId) return b.jobId;
  return null;
}

function layerFromRows(
  name: PgoLayerName,
  rows: Array<StepRecord | undefined>,
  jobRows: Array<StepRecord | undefined>,
): PgoLayer | null {
  const present = rows.filter((r): r is StepRecord => !!r);
  if (present.length === 0) return null;
  const bad = present.find(
    (r) => r.state === "degraded" || r.state === "failed",
  );
  const jobId = lastJob(...jobRows);
  if (bad) {
    const text = stripCausePrefix(bad.reason ?? `${bad.name} ${bad.state}`);
    return { name, state: "failed", text, jobId: jobIn(text) ?? jobId };
  }
  if (present.every((r) => r.state === "skipped")) {
    const reason = present.find((r) => r.reason)?.reason ?? "not run";
    return {
      name,
      state: /^disabled/i.test(reason) ? "disabled" : "off",
      text: reason,
      jobId: null,
    };
  }
  if (present.some((r) => r.state === "pending" || r.state === "running"))
    return null; // still in progress: no verdict yet
  return {
    name,
    state: "ok",
    text: present.find((r) => r.state === "ok" && r.reason)?.reason ?? "",
    jobId,
  };
}

/** The layers of one attempt (empty when there is nothing to say). */
export function pgoLayers(input: {
  steps: unknown;
  /** publish_versions.pgo_degraded_reason */
  reason: string | null | undefined;
  /** publish_versions.pgo_collect_job_id */
  collectJobId: string | null | undefined;
  /** publish_versions.pgo_status */
  status: string | null | undefined;
}): PgoLayer[] {
  const steps = parseSteps(input.steps);
  const by = (id: string) => steps?.find((s) => s.id === id);
  if (steps && (by("type-profile") || by("llvm-instrumented"))) {
    return [
      layerFromRows(
        "Type profile",
        [
          by("type-profile"),
          by("type-profile-training"),
          by("type-profile-applied"),
        ],
        [by("type-profile")],
      ),
      layerFromRows(
        "LLVM PGO",
        [by("llvm-pgo"), by("llvm-instrumented"), by("llvm-training")],
        [by("llvm-instrumented")],
      ),
      layerFromRows("Text order", [by("text-order")], []),
    ].filter((l): l is PgoLayer => l !== null);
  }
  // Older attempt: split the stored reason by layer prefix.
  const lines = (input.reason ?? "").split("\n").filter((l) => l.trim());
  if (input.status !== "degraded" && lines.length === 0) return [];
  const typeLines: string[] = [];
  const llvmLines: string[] = [];
  const orderLines: string[] = [];
  for (const l of lines) {
    if (/^llvm-pgo:/i.test(l)) llvmLines.push(stripCausePrefix(l));
    else if (/^text-order:/i.test(l)) orderLines.push(stripCausePrefix(l));
    else typeLines.push(l);
  }
  const out: PgoLayer[] = [];
  if (typeLines.length)
    out.push({
      name: "Type profile",
      state: "failed",
      text: typeLines.join(" "),
      jobId: input.collectJobId ?? jobIn(typeLines.join(" ")),
    });
  else if (input.collectJobId)
    out.push({
      name: "Type profile",
      state: "ok",
      text: "",
      jobId: input.collectJobId,
    });
  if (llvmLines.length)
    out.push({
      name: "LLVM PGO",
      state: "failed",
      text: llvmLines.join(" "),
      jobId: jobIn(llvmLines.join(" ")),
    });
  if (orderLines.length)
    out.push({
      name: "Text order",
      state: "failed",
      text: orderLines.join(" "),
      jobId: null,
    });
  return out;
}

/** "Type profile: ok (job b0e11027)" / "LLVM PGO: failed — <cause>" /
 *  "LLVM PGO: disabled by operator (…)". */
export function layerLine(l: PgoLayer): string {
  const job = l.jobId ? ` (job ${l.jobId.slice(0, 8)})` : "";
  switch (l.state) {
    case "ok":
      return `${l.name}: ok${job}${l.text ? ` — ${l.text}` : ""}`;
    case "failed":
      return `${l.name}: failed — ${l.text}`;
    case "disabled":
      return `${l.name}: ${l.text}`;
    default:
      return `${l.name}: off — ${l.text}`;
  }
}

/** The panel headline: names the failed layer(s) and says what ran fine. */
export function pgoHeadline(layers: readonly PgoLayer[]): string {
  const failed = layers.filter((l) => l.state === "failed");
  if (failed.length === 0) return "PGO";
  return `PGO: degraded — ${failed.map((l) => l.name).join(" + ")} failed`;
}
